import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
} from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import { getLandmarkViewsByDateRange } from '../../api/landmark'
import * as XLSX from 'xlsx'
import { FileDownload } from '@mui/icons-material'

type ViewDaily = {
  date: string
  viewCount: number
}

type Props = {
  landmarkId: number
}

export default function LandmarkViewAnalytics({ landmarkId }: Props) {
  // default = เดือนนี้
  const [start, setStart] = useState<Dayjs>(dayjs().startOf('month'))
  const [end, setEnd] = useState<Dayjs>(dayjs().endOf('month'))
  const [mode, setMode] = useState<'table' | 'chart'>('table')
  const [data, setData] = useState<ViewDaily[]>([])

  useEffect(() => {
    loadData()
  }, [start, end])

  const buildDateRange = (start: Dayjs, end: Dayjs) => {
    const dates: string[] = []
    let current = start.startOf('day')
    const last = end.startOf('day')

    while (current.diff(last, 'day') <= 0) {
      dates.push(current.format('YYYY-MM-DD'))
      current = current.add(1, 'day')
    }

    return dates
  }

  const normalizeViewDaily = (
    rawData: ViewDaily[],
    start: Dayjs,
    end: Dayjs
  ): ViewDaily[] => {
    const map = new Map<string, number>()

    rawData.forEach((d) => {
      map.set(dayjs(d.date).format('YYYY-MM-DD'), d.viewCount)
    })

    return buildDateRange(start, end).map((date) => ({
      date,
      viewCount: map.get(date) ?? 0,
    }))
  }

  const clampEndToToday = (end: Dayjs) => {
    const today = dayjs().startOf('day')
    const safeEnd = end.startOf('day')

    return safeEnd.diff(today, 'day') > 0 ? today : safeEnd
  }

  const loadData = async () => {
    try {
      const safeEnd = clampEndToToday(end)



      const result = await getLandmarkViewsByDateRange(
        landmarkId,
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD')
      )

      const normalized = normalizeViewDaily(result, start, safeEnd)

      setData(normalized)
    } catch (e) {
      console.error(e)
    }
  }

  const tableData = [...data].sort(
    (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
  )

  const getTableDiff = (index: number) => {
    if (index === tableData.length - 1) return null
    return tableData[index].viewCount - tableData[index + 1].viewCount
  }



  const diffs = data.map((d, i) =>
    i === 0 ? null : d.viewCount - data[i - 1].viewCount
  )

  const total = data.reduce((sum, d) => sum + d.viewCount, 0)

  const exportToExcel = () => {
    if (!data.length) return

    const rows = tableData.map((row, index) => {
      const diff = getTableDiff(index)

      return {
        วันที่: dayjs(row.date).format('DD/MM/YYYY'),
        จำนวนผู้เยี่ยมชม: row.viewCount,
        เปลี่ยนแปลง:
          diff === null
            ? ''
            : diff > 0
              ? `+${diff}`
              : diff,
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Landmark Views')

    const fileName = `landmark_${landmarkId}_${start.format(
      'YYYYMMDD'
    )}_${end.format('YYYYMMDD')}.xlsx`

    XLSX.writeFile(workbook, fileName)
  }


  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">

            <TextField
              type="date"
              label="เริ่มวันที่"
              value={start.format('YYYY-MM-DD')}
              onChange={(e) => setStart(dayjs(e.target.value))}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
            />

            <TextField
              type="date"
              label="ถึงวันที่"
              value={end.format('YYYY-MM-DD')}
              onChange={(e) => setEnd(dayjs(e.target.value))}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
            />

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
            >
              <ToggleButton value="table">ตาราง</ToggleButton>
              <ToggleButton value="chart">กราฟ</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
          >
            ส่งออก Excel
          </Button>

        </Stack>

        {/* Summary */}
        <Typography variant="subtitle1">
          มีผู้เข้าชมทั้งหมด: <strong>{total.toLocaleString()}</strong> ครั้ง
        </Typography>

        {/* TABLE */}
        {mode === 'table' && (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>วันที่</TableCell>
                  <TableCell align="right">จำนวนผู้เยี่ยมชม</TableCell>
                  <TableCell align="right">เปลี่ยนแปลง</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => {
                  const diff = getTableDiff(index)

                  return (
                    <TableRow key={row.date}>
                      <TableCell>
                        {dayjs(row.date).format('DD/MM/YYYY')}
                      </TableCell>

                      <TableCell align="right">
                        {row.viewCount.toLocaleString()}
                      </TableCell>

                      <TableCell align="right">
                        {diff === null ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : diff > 0 ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            alignItems="center"
                            sx={{ color: 'success.main', fontWeight: 600 }}
                          >
                            <span>▲</span>
                            <span>+{diff.toLocaleString()}</span>
                          </Stack>
                        ) : diff < 0 ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            alignItems="center"
                            sx={{ color: 'error.main', fontWeight: 600 }}
                          >
                            <span>▼</span>
                            <span>{diff.toLocaleString()}</span>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>

            </Table>
          </TableContainer>
        )}

        {/* CHART */}
        {mode === 'chart' && (
          <Box height={300}>
            <LineChart
              xAxis={[
                {
                  scaleType: 'point',
                  data: data.map((d) => dayjs(d.date).format('DD/MM')),
                },
              ]}
              series={[
                {
                  data: data.map((d) => d.viewCount),
                  label: 'จำนวนผู้เยี่ยมชม',
                  valueFormatter: (value: any, context: any) => {
                    const index = context.dataIndex
                    const diff = diffs[index]

                    if (diff == null) {
                      return `${value.toLocaleString()} ครั้ง`
                    }

                    const sign = diff > 0 ? '+' : ''
                    return `${value.toLocaleString()} ครั้ง (${sign}${diff.toLocaleString()})`
                  },
                },
              ]}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
